import React from 'react';
import { Link } from 'react-router-dom';
import { useNewsArticles } from '../hooks/useNewsArticles';

const EXCERPT_LENGTH = 200;
function excerpt(text: string): string {
  if (text.length <= EXCERPT_LENGTH) return text;
  return text.slice(0, EXCERPT_LENGTH).trimEnd() + '…';
}

const Nyheter: React.FC = () => {
  const { articles, loading, error, hasMore, loadMore, loadingMore } = useNewsArticles();

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('nb-NO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  const featured = articles[0] ?? null;
  const rest = articles.slice(1);

  return (
    <div className="bg-[#080808] text-white min-h-screen section-padding overflow-hidden">
      <div className="container-responsive max-w-5xl">

        {/* Page header */}
        <div className="mb-10" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '1.5rem' }}>
          <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: 'rgba(255,255,255,0.3)' }}>Smuglergods</p>
          <h1 className="heading-responsive font-bold text-white tracking-tight">Nyheter</h1>
        </div>

        {loading ? (
          <div className="text-center py-24" style={{ color: 'rgba(255,255,255,0.4)' }}>Laster nyheter…</div>
        ) : error ? (
          <p className="text-red-400">{error}</p>
        ) : articles.length === 0 ? (
          <p style={{ color: 'rgba(255,255,255,0.4)' }}>Ingen nyheter publisert ennå.</p>
        ) : (
          <>
            {/* ── Featured top story ── */}
            {featured && (
              <Link
                to={`/nyheter/${featured.id}`}
                className="group block mb-12"
              >
                {featured.image_url && (
                  <div className="w-full overflow-hidden mb-5" style={{ maxHeight: '480px' }}>
                    <img
                      src={featured.image_url}
                      alt={featured.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                      style={{ maxHeight: '480px' }}
                      loading="eager"
                    />
                  </div>
                )}
                <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: 'rgba(255,255,255,0.35)' }}>
                  Toppnyhet
                </p>
                <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight leading-tight mb-3 group-hover:underline">
                  {featured.title}
                </h2>
                <p className="text-base mb-3 leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  {excerpt(featured.content)}
                </p>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
                  {featured.author && <span className="font-semibold text-white/50">{featured.author} · </span>}
                  {formatDate(featured.created_at)}
                </p>
              </Link>
            )}

            {/* ── Divider ── */}
            {rest.length > 0 && (
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', marginBottom: '2rem' }} />
            )}

            {/* ── Remaining articles ── */}
            <div>
              {rest.map((article, i) => (
                <React.Fragment key={article.id}>
                  <Link
                    to={`/nyheter/${article.id}`}
                    className="group flex gap-5 py-6"
                  >
                    {/* Thumbnail */}
                    {article.image_url ? (
                      <div className="shrink-0 overflow-hidden" style={{ width: '120px', height: '80px' }}>
                        <img
                          src={article.image_url}
                          alt={article.title}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                    ) : (
                      <div
                        className="shrink-0"
                        style={{ width: '120px', height: '80px', background: 'rgba(255,255,255,0.04)' }}
                      />
                    )}

                    {/* Text */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-white text-base sm:text-lg leading-snug tracking-tight mb-1 group-hover:underline">
                        {article.title}
                      </h3>
                      <p className="text-sm leading-relaxed line-clamp-2 mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>
                        {excerpt(article.content)}
                      </p>
                      <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
                        {article.author && <span className="font-semibold" style={{ color: 'rgba(255,255,255,0.4)' }}>{article.author} · </span>}
                        {formatDate(article.created_at)}
                      </p>
                    </div>
                  </Link>
                  {i < rest.length - 1 && (
                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }} />
                  )}
                </React.Fragment>
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center pt-8">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="btn-secondary py-3 px-8 font-bold disabled:opacity-50"
                >
                  {loadingMore ? 'Laster…' : 'Vis mer'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Nyheter;
